import React from 'react';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Checkbox from '@material-ui/core/Checkbox';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/react-hooks';
import CircularProgress from '@material-ui/core/CircularProgress';

import Tag from 'components/common/tag';
import Pagination from 'components/common/pagination';
import Modal from 'components/common/modal';
import { ReactComponent as AddIcon } from 'assets/vectors/add-green-16px.svg';
import useEquipmentStatus from 'helpers/useEquipmentStatus';
import Filters from 'components/common/equipment/filters';
import { ReactComponent as OrderDownIcon } from 'assets/vectors/order-down-16px.svg';

const StsBoxesQuery = gql`
  query StsBoxes ($first: Int, $after: String, $last: Int, $before: String, $sortBy: [String], $status: String, $location: String){
    stsboxes(first: $first, after: $after, last: $last, before: $before, sortBy: $sortBy, status: $status, location: $location) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
      }
      edges {
        node {
          id
          plNumber
          location
          contents
          status
          lastProducts
        }
      }
    }
  }
`;

const AddStsBoxesMutation = gql`
  mutation AddStsProjectEquipment($input: AddStsProjectEquipmentInput!) {
    addStsProjectEquipment(addStsProjectEquipmentData: $input) {
      success
    }
  }
`;

const resultCount = 10;

function StsBoxesModal({ setShowStsBoxesModal, projectId, refetch, addedStsBoxes, onAdd }) {
  const [selectedStsBoxes, setSelectedStsBoxes] = React.useState([]);
  const [paginationVariables, setPaginationVariables] = React.useState({first: resultCount});
  const {tagColor, equipmentStatusLabel} = useEquipmentStatus();
  const [filters, setFilters] = React.useState([]);
  const [sortBy, setSortBy] = React.useState({ type: 'plNumber', direction: 'asc' });

  const filterVariables = React.useMemo(() => {
    return filters.reduce((prev, cur) => {
      return {
        ...prev,
        [cur.key]: cur.val,
      }
    }, {});
  }, [filters]);

  const { data, loading: listLoading } = useQuery(StsBoxesQuery, {
    variables: {
      ...paginationVariables,
      ...filterVariables,
      sortBy: [`${sortBy.type} ${sortBy.direction}`]
    },
    fetchPolicy: 'network-only',
  });

  const [addStsBoxes, { loading: addLoading }] = useMutation(AddStsBoxesMutation, {
    variables: {
      input: {
        stsprojectId: projectId,
        equipmentIds: selectedStsBoxes,
        equipmentType: 'stsbox',
      },
    },
    onCompleted() {
      refetch();
      onAdd && onAdd();
      setShowStsBoxesModal(false);
    },
    onError(e) {
      console.log(e)
    }
  });

  const stsBoxes = data ? data.stsboxes.edges.map(u => u.node) : [];
  const pageInfo = data ? data.stsboxes.pageInfo : {};

  function checkboxHandler(stsBoxId) {
    if (selectedStsBoxes.includes(stsBoxId)) {
      setSelectedStsBoxes(selectedStsBoxes.filter(id => id !== stsBoxId));
    } else {
      setSelectedStsBoxes([...selectedStsBoxes, stsBoxId]);
    }
  }

  function checkboxDisabled(stsBox) {
    return addedStsBoxes.includes(stsBox.id) || stsBox.status !== 'stand_by';
  }

  function sortHandler(type) {
    if (type !== sortBy.type) {
      setSortBy({ type, direction: 'desc' });
    } else if (type === sortBy.type && sortBy.direction === 'desc') {
      setSortBy({ ...sortBy, direction: 'asc'});
    } else if (type === sortBy.type && sortBy.direction === 'asc') {
      setSortBy({ ...sortBy, direction: 'desc'});
    }
  }

  const addButtonDisabled = listLoading || addLoading || !selectedStsBoxes.length

  return (
    <Modal onClose={() => setShowStsBoxesModal(false)} title="STS BOXES" style={{ width: '90%'}} preventOutsideClick>
      <Title>ADD STS BOXES</Title>
      <Filters filters={filters} setFilters={setFilters} equipmentType="stsbox" />
      {
        listLoading
          ? <CircularProgress size={64} style={{ margin: '128px auto', display: 'block' }} />
          : (
            <>
              <TableContainer>
                <Table aria-label="simple table" style={{ whiteSpace: 'nowrap' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('plNumber')}>
                        PL Nr. <StyledOrderIcon active={sortBy.type === 'plNumber' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('location')}>
                        Location <StyledOrderIcon active={sortBy.type === 'location' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('contents')}>
                        Contents <StyledOrderIcon active={sortBy.type === 'contents' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('status')}>
                        Status <StyledOrderIcon active={sortBy.type === 'status' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell>
                        Last products
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                  {
                    !stsBoxes.length
                      ? <TableRow>
                          <TableCell>No results</TableCell>
                          <TableCell />
                          <TableCell />
                          <TableCell />
                          <TableCell />
                        </TableRow>
                      : (
                        stsBoxes.map(stsBox => (
                          <TableRow key={stsBox.id}>
                            <TableCell>
                              <Checkbox
                                checked={selectedStsBoxes.includes(stsBox.id)}
                                onChange={() => checkboxHandler(stsBox.id)}
                                name="Now"
                                color="primary"
                                disabled={checkboxDisabled(stsBox)}
                              />
                            </TableCell>
                            <TableCell>
                              <Link target="_blank" href={`/dashboard/equipment/stsBoxes/${stsBox.id}`}>{stsBox.plNumber}</Link>
                            </TableCell>
                            <TableCell>
                              {stsBox.location}
                            </TableCell>
                            <TableCell style={{ maxWidth: '300px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                              {stsBox.contents}
                            </TableCell>
                            <TableCell>
                              <Tag color={tagColor(stsBox.status)} label={equipmentStatusLabel(stsBox.status)} />
                            </TableCell>
                            <TableCell>
                              {stsBox.lastProducts}
                            </TableCell>
                          </TableRow>
                        ))
                      )
                    }
                  </TableBody>
                </Table>
              </TableContainer>
              <Pagination
                paginationVariables={paginationVariables}
                setPaginationVariables={setPaginationVariables}
                resultCount={resultCount}
                pageInfo={pageInfo}
              />
              <ButtonWrapper>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={addStsBoxes}
                  endIcon={<AddIcon />}
                  disabled={addButtonDisabled}
                >
                Add
                </Button>
              </ButtonWrapper>
            </>
          )
      }
    </Modal>
  );
}

const Link = styled.a`
  color: ${props => props.theme.colors.primaryColor};
  text-decoration: none;
`;

const Title = styled.div`
  color: ${props => props.theme.colors.primaryColor};
  font-weight: 700;
  margin-bottom: 36px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;

  @media (${props => props.theme.breakpoints.tablet}) {
    justify-content: flex-start;
  }
`;

const StyledOrderIcon = styled(OrderDownIcon)`
  display: ${props => props.active ? 'inline-flex' : 'none'};
  transform: ${props => props.direction === 'desc' ? 'none' : 'rotate(180deg)'};
  margin-left: 4px;
  position: relative;
  top: 4px;
`;


export default StsBoxesModal;
